import { useEffect, useRef, useState } from 'react';
import { Hint } from '../../components/ui/hint';
import { api, errorLine } from '../../api/client';
import type {
  ChargingView,
  ConfigureCharging,
  CreditChargeRow,
  CreditOperation,
  CreditRateKey,
  PublishRate,
} from '../../api/types';
import { definitelyRejected, formatCredits } from '../../lib/credits';

const OUTCOMES: Record<string, string> = {
  delivered: 'Charged: delivered',
  no_confirmed_match: 'Returned: no confirmed match',
  not_delivered: 'Returned: not delivered',
  continuation_not_started: 'Returned: research did not start',
  failed: 'Returned: failed',
  cancelled: 'Returned: stopped by the office',
  identity_withdrawn: 'Returned: identity rejected',
  run_removed: 'Returned: record deleted',
  released_by_operator: 'Returned by an operator',
  undone: 'Returned: import undone',
};
const OPERATIONS: Record<CreditOperation, string> = {
  quick_look: 'Quick look',
  person_research: 'Person research',
  research_recheck: 'Research re-check',
  org_investigation: 'Organization investigation',
  event_research: 'Event research',
  travel_brief: 'Travel brief',
  event_scan: 'Attendee scan',
  import: 'Import',
};
const RATES: Record<CreditRateKey, string> = {
  quick_look: 'Quick look',
  person_research: 'Person research',
  research_recheck: 'Research re-check',
  org_investigation: 'Organization investigation',
  event_research: 'Event research',
  travel_brief: 'Travel brief',
  event_attendee: 'Scan: attendee searched on the web',
  event_attendee_on_record: 'Scan: attendee rated from the record',
  import_rows: 'Import: each 10 rows beyond the month’s included rows',
};
/** A scan and an import hold their maximum while open: a scan is charged for the
 * attendees it screened, an import for the rows it wrote. */
const price = (c: CreditChargeRow) =>
  c.operation === 'event_scan' && c.basis === 'allowance'
    ? 'No charge'
    : c.operation === 'import' && c.basis === 'allowance'
      ? 'Included rows'
      : c.basis !== 'credit'
        ? 'Included'
        : (c.operation === 'event_scan' || c.operation === 'import') && c.state === 'open'
          ? `Up to ${formatCredits(c.units)} credit`
          : `${formatCredits(c.units)} credit`;

/** Credits as typed, in ledger units; zero allowed (a free attendee class). */
function rateUnits(text: string): number | null {
  const match = /^(\d+)(?:\.(\d{1,3}))?$/.exec(text.trim());
  if (!match) return null;
  const units = Number(match[1]) * 1000 + Number((match[2] ?? '').padEnd(3, '0'));
  return Number.isSafeInteger(units) && units <= 1_000_000_000 ? units : null;
}

/**
 * Live charging (credits launch spec, steps 1–4): the enrollment switch and plan,
 * this month's quick looks and import rows, the monthly plan credits, the catalog and its
 * publish form, and every open charge with what the record says should happen to it. Actions only apply what the durable record supports:
 * reconcile settles a delivered run or returns an undelivered one; release returns
 * a charge whose run has not delivered; dispatch re-offers a run never picked up.
 */
export function CreditChargingPanel({ orgId, onChange }: { orgId: string; onChange?: () => void }) {
  const [view, setView] = useState<ChargingView | null>(null);
  const [planKey, setPlanKey] = useState('');
  const [reason, setReason] = useState('');
  const [releaseReason, setReleaseReason] = useState('');
  const [rateKey, setRateKey] = useState<CreditRateKey>('event_attendee');
  const [rateVertical, setRateVertical] = useState<'default' | 'org'>('default');
  const [rateCredits, setRateCredits] = useState('');
  const [rateReason, setRateReason] = useState('');
  const [pendingRate, setPendingRate] = useState<PublishRate | null>(null);
  const [pending, setPending] = useState<ConfigureCharging | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const lock = useRef(false);

  useEffect(() => {
    let live = true;
    api
      .charging(orgId)
      .then((v) => {
        if (!live) return;
        setView(v);
        setPlanKey(v.setting?.plan?.key ?? '');
      })
      .catch((e) => {
        if (live) setError(errorLine(e));
      });
    return () => {
      live = false;
    };
  }, [orgId]);

  async function action(work: () => Promise<string | void>) {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const message = await work();
      setView(await api.charging(orgId));
      if (message) setNotice(message);
      onChange?.();
    } catch (e) {
      setError(errorLine(e));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }

  async function configure(enabled: boolean) {
    if (!view) return;
    const body = pending ?? {
      command_key: crypto.randomUUID(),
      expected_version: view.setting?.version ?? 0,
      enabled,
      plan_key: planKey || null,
      reason: reason.trim(),
    };
    setPending(body);
    try {
      const saved = await api.configureCharging(orgId, body);
      setPending(null);
      setReason('');
      return `Charging version ${saved.version} recorded.`;
    } catch (e) {
      if (definitelyRejected(e)) setPending(null);
      throw e;
    }
  }

  /** Publish a new catalog version: platform-wide, for new admissions only. */
  async function publish() {
    if (!view) return;
    const vertical = rateVertical === 'org' ? view.vertical : null;
    const latest = view.rates.find((r) => r.operation === rateKey && r.vertical === vertical);
    const body = pendingRate ?? {
      command_key: crypto.randomUUID(),
      operation: rateKey,
      vertical,
      expected_version: latest?.version ?? 0,
      units: rateUnits(rateCredits) ?? 0,
      reason: rateReason.trim(),
    };
    setPendingRate(body);
    try {
      const saved = await api.publishRate(body);
      setPendingRate(null);
      setRateCredits('');
      setRateReason('');
      return `${RATES[saved.operation]} is now ${formatCredits(saved.units)} credits (version ${saved.version}).`;
    } catch (e) {
      if (definitelyRejected(e)) setPendingRate(null);
      throw e;
    }
  }

  const current = view?.setting ?? null;
  const planChosen = !!(pending?.plan_key ?? planKey);
  return (
    <section className="credit-card credit-charging">
      <div className="credit-card-head">
        <h3>Live charging</h3>
        <span className="credit-badge">
          {!view
            ? 'Loading…'
            : current?.enabled
              ? `Charging on · ${current.plan?.label} plan`
              : 'Charging off'}
        </span>
      </div>
      <Hint label="About live charging">
        Enrolled accounts pay from their credit balance for research, and for quick looks beyond
        their plan’s monthly allowance. A run is charged only when it delivers: a search that
        confirms who the person is, a finished dossier, lookup, event research or travel brief.
        Failures and stopped runs return their hold. An attendee scan holds its maximum and is
        charged for the attendees it screened, however it ends. An import holds its rows beyond the
        month’s included rows and is charged for the rows it wrote; undoing it returns the charge.
        Each enrolled account receives its plan’s credits once a month, and what is left of them
        lapses when the month ends; credits granted by hand do not lapse. Accounts that are not
        enrolled are not affected.
      </Hint>
      {view?.allowance ? (
        <p>
          {view.allowance.used} of {view.allowance.included} quick looks used in{' '}
          {view.allowance.month}
          {view.quick_look_rate
            ? ` · beyond that, ${formatCredits(view.quick_look_rate.units)} credit each`
            : ''}
          <br />
          {view.allowance.import_rows_used} of {view.allowance.import_rows_included} import rows
          used in {view.allowance.month}
        </p>
      ) : null}
      {view && view.monthly_grants.length > 0 ? (
        <details>
          <summary>Monthly plan credits</summary>
          <ul>
            {view.monthly_grants.map((g) => (
              <li key={`${g.month}-${g.created_at}`}>
                {g.month} · {g.plan_label}: {formatCredits(g.units)} credits granted{' '}
                {new Date(g.created_at).toLocaleString()} · {formatCredits(g.available)} left,
                lapsing {new Date(g.expires_at).toLocaleString()}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {view && view.rates.length > 0 ? (
        <details>
          <summary>Catalog</summary>
          <ul>
            {view.rates.map((r) => (
              <li key={r.id}>
                {RATES[r.operation]} · {r.vertical ?? 'default'}: {formatCredits(r.units)} credits
                (version {r.version}){r.reason ? ` · ${r.reason}` : ''}
              </li>
            ))}
          </ul>
          <Hint label="About the catalog">
            Rates apply to every organization. A vertical’s own rate outranks the default. A new
            version prices new admissions only; runs already admitted keep the price they were
            admitted at. This organization’s vertical is {view.vertical}.
          </Hint>
          <div className="field">
            <label className="label" htmlFor="rate-key">
              Rate
            </label>
            <select
              id="rate-key"
              value={pendingRate?.operation ?? rateKey}
              disabled={busy || !!pendingRate}
              onChange={(e) => setRateKey(e.target.value as CreditRateKey)}
            >
              {(Object.keys(RATES) as CreditRateKey[]).map((key) => (
                <option key={key} value={key}>
                  {RATES[key]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="rate-vertical">
              Applies to
            </label>
            <select
              id="rate-vertical"
              value={pendingRate ? (pendingRate.vertical ? 'org' : 'default') : rateVertical}
              disabled={busy || !!pendingRate}
              onChange={(e) => setRateVertical(e.target.value as 'default' | 'org')}
            >
              <option value="default">Every vertical without its own rate (default)</option>
              <option value="org">The {view.vertical} vertical only</option>
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="rate-credits">
              Credits
            </label>
            <input
              type="text"
              id="rate-credits"
              inputMode="decimal"
              placeholder="For example: 4"
              value={pendingRate ? formatCredits(pendingRate.units) : rateCredits}
              disabled={busy || !!pendingRate}
              onChange={(e) => setRateCredits(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="rate-reason">
              Reason for the new price
            </label>
            <input
              type="text"
              id="rate-reason"
              placeholder="For example: Retuned after the first security event"
              value={pendingRate?.reason ?? rateReason}
              maxLength={1000}
              disabled={busy || !!pendingRate}
              onChange={(e) => setRateReason(e.target.value)}
            />
          </div>
          <div className="form-actions">
            <button
              className="btn"
              type="button"
              disabled={
                busy || (!pendingRate && (rateUnits(rateCredits) === null || !rateReason.trim()))
              }
              onClick={() => void action(publish)}
            >
              {pendingRate ? 'Retry publish' : 'Publish new rate'}
            </button>
          </div>
          {pendingRate ? <p>Unconfirmed publish. Retry to recover the same request.</p> : null}
        </details>
      ) : null}
      <div className="field">
        <label className="label" htmlFor="charging-plan">
          Plan
        </label>
        <select
          id="charging-plan"
          value={pending?.plan_key ?? planKey}
          disabled={busy || !!pending || !view}
          onChange={(e) => setPlanKey(e.target.value)}
        >
          <option value="">No plan</option>
          {view?.plans.map((p) => (
            <option key={p.id} value={p.key}>
              {p.label}: {formatCredits(p.monthly_grant_units)} credits, {p.quick_look_allowance}{' '}
              quick looks and {p.import_row_allowance} import rows a month
              {p.paid_work ? '' : ', no new paid work'}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label className="label" htmlFor="charging-reason">
          Reason for the change
        </label>
        <input
          type="text"
          id="charging-reason"
          placeholder="For example: Pilot enrollment agreed on the 2026-09 call"
          value={reason}
          maxLength={1000}
          disabled={busy || !!pending || !view}
          onChange={(e) => setReason(e.target.value)}
        />
        <Hint label="About the charging reason">
          Internal audit note. Customers do not see this.
        </Hint>
      </div>
      <div className="form-actions">
        <button
          className="btn"
          type="button"
          disabled={busy || !view || (!pending && (!reason.trim() || !planChosen))}
          onClick={() => void action(() => configure(true))}
        >
          {pending
            ? 'Retry change'
            : current?.enabled
              ? 'Save plan, charging on'
              : 'Turn charging on'}
        </button>{' '}
        <button
          className="btn"
          type="button"
          disabled={busy || !view || !!pending || !reason.trim()}
          onClick={() => void action(() => configure(false))}
        >
          {current?.enabled ? 'Turn charging off' : 'Save plan, charging off'}
        </button>
      </div>
      {pending ? <p>Unconfirmed change. Retry to recover the same request.</p> : null}
      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}

      <h4>Open charges</h4>
      {view && view.open.length === 0 ? <p>No open charges.</p> : null}
      {view && view.open.length > 0 ? (
        <>
          <div className="field">
            <label className="label" htmlFor="charge-release-reason">
              Reason for releasing a hold
            </label>
            <input
              type="text"
              id="charge-release-reason"
              placeholder="For example: Engine outage, run will not start"
              value={releaseReason}
              maxLength={1000}
              disabled={busy}
              onChange={(e) => setReleaseReason(e.target.value)}
            />
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Run</th>
                <th>Price</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {view.open.map((c) => (
                <tr key={c.id}>
                  <td>
                    {c.subject_name ?? 'Removed record'}
                    <div>
                      {OPERATIONS[c.operation]} · {new Date(c.created_at).toLocaleString()}
                    </div>
                    <details className="row-detail">
                      <summary>{c.import_id ? 'Import ID' : 'Run ID'}</summary>
                      {c.run_id ?? c.import_id}
                    </details>
                  </td>
                  <td>{price(c)}</td>
                  <td>{c.reason}</td>
                  <td>
                    {c.resolution !== 'wait' ? (
                      <button
                        className="btn"
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void action(async () => {
                            const r = await api.reconcileCharge(orgId, c.id);
                            return r.outcome ? OUTCOMES[r.outcome] : 'Still waiting on the run.';
                          })
                        }
                      >
                        {c.resolution === 'settle' ? 'Charge it' : 'Return the hold'}
                      </button>
                    ) : null}
                    {c.reason_code === 'import_stopped' ? (
                      <button
                        className="btn"
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void action(async () => {
                            const r = await api.reconcileCharge(orgId, c.id);
                            return r.outcome ? OUTCOMES[r.outcome] : 'Still waiting on the import.';
                          })
                        }
                      >
                        Charge what it wrote
                      </button>
                    ) : null}
                    {c.reason_code === 'awaiting_dispatch' ? (
                      <button
                        className="btn"
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          void action(async () => {
                            const r = await api.dispatchCharge(orgId, c.id);
                            return r.enqueued
                              ? 'Dispatched again.'
                              : 'Already queued; nothing new was sent.';
                          })
                        }
                      >
                        Dispatch again
                      </button>
                    ) : null}
                    {c.resolution !== 'settle' ? (
                      <button
                        className="btn"
                        type="button"
                        disabled={busy || !releaseReason.trim()}
                        onClick={() =>
                          void action(async () => {
                            await api.releaseCharge(orgId, c.id, releaseReason.trim());
                            setReleaseReason('');
                            return 'Hold released.';
                          })
                        }
                      >
                        Release
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      <details>
        <summary>Recent charges (up to 50)</summary>
        <ul>
          {view?.recent.map((c) => (
            <li key={c.id}>
              {c.subject_name ?? 'Removed record'} · {OPERATIONS[c.operation]} ·{' '}
              {new Date(c.created_at).toLocaleString()} · {price(c)} ·{' '}
              {c.outcome ? OUTCOMES[c.outcome] : 'Open'}
            </li>
          ))}
        </ul>
      </details>
      <details>
        <summary>Charging changes (up to 20)</summary>
        <ul>
          {view?.history.map((s) => (
            <li key={s.id}>
              Version {s.version}: {s.enabled ? 'on' : 'off'} · {s.plan?.label ?? 'no plan'} ·{' '}
              {s.reason} · {new Date(s.created_at).toLocaleString()}
            </li>
          ))}
        </ul>
      </details>
    </section>
  );
}
